import { useCallback, useEffect, useState } from 'react';
import { feedbackArchive, type ArchivePage } from '../api';
import type { FeedbackTranslate } from '../i18n';
import { Button, Card, DataTable, Field, Moment, Notice, Select, TextInput } from '../../../shell/kit';
import { useToast } from '../../../shell/toast';
import { errorText } from '../../../shell/errors';

const PAGE = 50;

/**
 * Архив обращений из прежнего сервиса (ТЗ §1 п.4). Импорт складывал историю в
 * базу, но прочитать её было негде; теперь она ищется и листается здесь.
 */
export function ArchiveView({ t }: { t: FeedbackTranslate }) {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [applied, setApplied] = useState('');
  const [channel, setChannel] = useState('');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<ArchivePage | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPage(await feedbackArchive({ ...(applied ? { q: applied } : {}), ...(channel ? { channel } : {}), offset, limit: PAGE }));
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setLoading(false);
    }
  }, [applied, channel, offset, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const total = page?.total ?? 0;

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('feedback.arch.hint')}</Notice>
      <Card title={t('feedback.arch.title')} hint={t('feedback.arch.found', { n: total })}>
        <form
          className="mb-3 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setOffset(0);
            setApplied(query.trim());
          }}
        >
          <div className="min-w-72 flex-1">
            <Field label={t('feedback.arch.search')}>
              <TextInput value={query} onChange={(e) => setQuery(e.target.value)} />
            </Field>
          </div>
          <Field label={t('feedback.queue.channel')}>
            <Select
              value={channel}
              onChange={(e) => {
                setOffset(0);
                setChannel(e.target.value);
              }}
            >
              <option value="">{t('feedback.queue.all')}</option>
              {(page?.channels ?? []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Button tone="primary" type="submit">
            {t('feedback.arch.search')}
          </Button>
        </form>

        <DataTable
          loading={loading}
          rows={page?.items ?? []}
          empty={t('feedback.arch.empty')}
          rowKey={(r) => r.id}
          columns={[
            { key: 'at', header: t('feedback.arch.received'), render: (r) => <Moment iso={r.receivedAt} /> },
            { key: 'ch', header: t('feedback.queue.channel'), render: (r) => r.channel ?? '—' },
            {
              key: 'body',
              header: t('feedback.card.text'),
              render: (r) => (
                <div className="max-w-xl">
                  {r.subject ? <p className="text-xs font-medium text-ink">{r.subject}</p> : null}
                  <p className="whitespace-pre-line text-sm text-ink">{r.body}</p>
                </div>
              ),
            },
            {
              key: 'res',
              header: t('feedback.arch.resolution'),
              render: (r) => <span className="text-xs text-ink-muted">{r.resolution ?? '—'}</span>,
            },
          ]}
        />

        <div className="mt-3 flex items-center gap-2">
          <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            {t('feedback.arch.prev')}
          </Button>
          <span className="text-xs text-ink-muted">
            {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE, total)} / {total}
          </span>
          <Button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
            {t('feedback.arch.next')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
